import {useState, useEffect, useCallback, useMemo} from 'react';
import {FloatButton, Table} from 'antd';
import {APIv1} from '../../api';
import {Link, useOutletContext} from 'react-router-dom';
import {extractDateBySecond} from '../../utils';
import {extractStringDate} from '../../utils/date/dateUtils';
import {FileAddOutlined} from '@ant-design/icons';
import DevicesColumns from './device.constants';
import {useTranslation} from 'react-i18next';
import groupByMarketName from "../../utils/device/groupByMarketName";
import {getHandleSaveMarket, getHandleDeleteSaveMarket} from "../../handlers/savedMarketHandler";
import useUrlTableState from "../../hooks/useUrlTableState";
import ListStateIndicator from "../../components/ListStateIndicator/ListStateIndicator";
import './Device.scss';

const DEVICE_QUERY_FILTER_MAP = {
    is_active_period: 'is_active_period',
    device_serial__os_architecture_bit: 'device_serial__os_architecture_bit',
    device_serial__status: 'status',
};

const DEVICE_FILTER_LABELS = {
    is_active_period: 'Activity',
    device_serial__os_architecture_bit: 'Architecture',
    status: 'Status',
};

const DEVICE_FILTER_VALUE_PARSERS = {
    device_serial__os_architecture_bit: Number,
};

const getRuntimeValue = (device, key, legacyKey = key) => (
    device.runtime?.[key] ?? device[legacyKey]
);

const formatEndDate = (date) => date ? extractStringDate(date) : '-';

const normalizeDeviceStatus = (device) => {
    const runtime = device.runtime ?? {};
    const deviceSerial = device.serial_number ?? device.device_serial;

    return {
        key: device.id,
        device_serial: deviceSerial,
        company_name: device.company_name,
        is_active: runtime.is_active ?? device.is_active,
        is_active_time: runtime.is_active_time ?? device.is_active_time,
        is_shift_open: getRuntimeValue(device, 'is_shift_open') ?? false,
        teamviewer: getRuntimeValue(device, 'teamviewer') ?? '-',
        device_ip_addr: String(getRuntimeValue(device, 'ip_address', 'device_ip_address') ?? '-').replace(',', ' / '),
        terminal_id: getRuntimeValue(device, 'terminal_id') ?? '-',
        orders_not_sent_count: getRuntimeValue(device, 'orders_not_sent_count') ?? '-',
        z_report_left_count: getRuntimeValue(device, 'z_report_left_count') ?? '-',
        applet_version: getRuntimeValue(device, 'applet_version') ?? '-',
        updated_date: extractDateBySecond(runtime.updated_date ?? device.updated_date),
        end_date: formatEndDate(device.end_date),
        version_number: runtime.version_number ?? (device.version_number ? `${device.version_number}*` : '-'),
        device_serial__status: device.status ?? device.device_serial__status,
        market_id: device.market_id ?? device.market__id,
        market_name: device.market_name ?? device.market__name,
        is_saved: device.is_saved,
        last_order_number: device.last_order_number,
        device_serial__fbox_version: device.fbox_version ?? device.device_serial__fbox_version,
        device_serial__os_architecture_bit: device.os_architecture_bit ?? device.device_serial__os_architecture_bit,
        device_serial__last_order_time: extractDateBySecond(device.last_order_time ?? device.device_serial__last_order_time),
        pos_terminal: device.pos_terminal ?? {},
        temp: Math.floor(parseFloat(runtime.temperature ?? device.temperature) || 0),
    };
};

const Device = ({defaultPaginationSize = 20, companyInn}) => {
    const {t} = useTranslation();
    const {searchText} = useOutletContext();

    const [userData, setUserData] = useState(() => JSON.parse(localStorage.getItem('user')) || {});
    const [isUserStaff, setIsUserStaff] = useState(userData?.data?.is_admin || false);
    const [deviceStatusData, setDeviceStatusData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [totalMarkets, setTotalMarkets] = useState([]);
    const [totalDevices, setTotalDevices] = useState(0);
    const [isMobile, setIsMobile] = useState(false);
    const tableState = useUrlTableState({
        defaultPageSize: defaultPaginationSize,
        filterParamMap: DEVICE_QUERY_FILTER_MAP,
        filterValueParsers: DEVICE_FILTER_VALUE_PARSERS,
        filterLabels: DEVICE_FILTER_LABELS,
        searchText,
    });

    const handleSaveMarket = getHandleSaveMarket(setDeviceStatusData);
    const handleDeleteSaveMarket = getHandleDeleteSaveMarket(setDeviceStatusData);

    const columns = useMemo(
        () => DevicesColumns(t, isUserStaff, handleSaveMarket, handleDeleteSaveMarket, totalDevices, totalMarkets, companyInn, userData.token, isMobile),
        [t, isUserStaff, handleSaveMarket, handleDeleteSaveMarket, totalDevices, companyInn, userData.token, isMobile],
    );
    const controlledColumns = useMemo(
        () => tableState.controlledColumns(columns),
        [columns, tableState],
    );
    const fetchDeviceStatusData = useCallback(async () => {
        if (!userData.token) return;

        setLoading(true);

        try {
            const url = companyInn ? `/company/related/devices/${companyInn}/` : '/device/list/';

            const response = await APIv1.get(url, {
                params: {
                    page: tableState.currentPage,
                    page_size: tableState.pageSize,
                    search: tableState.search,
                    ordering: tableState.ordering,
                    ...tableState.filters,
                },
                headers: {Authorization: `Token ${userData.token}`},
            });

            const totalMarketsResponse = await APIv1.get('/market/count/');
            setTotalMarkets(totalMarketsResponse.data.count)

            const results = response.data.results ?? response.data;
            setDeviceStatusData(groupByMarketName(results.map(normalizeDeviceStatus)));

            setTotalDevices(response.data.count ?? results.length);
        } catch (error) {
            console.error('Error fetching device status data:', error);
        } finally {
            setLoading(false);
        }
    }, [companyInn, userData.token, tableState.currentPage, tableState.pageSize, tableState.search, tableState.ordering, tableState.filters]);

    useEffect(() => {
        fetchDeviceStatusData();
    }, [fetchDeviceStatusData, companyInn]);

    useEffect(() => {
        const mediaQuery = window.matchMedia('(max-width: 768px)');

        const handleViewportChange = (event) => {
            setIsMobile(event.matches);
        };

        handleViewportChange(mediaQuery);
        mediaQuery.addEventListener('change', handleViewportChange);

        return () => {
            mediaQuery.removeEventListener('change', handleViewportChange);
        };
    }, []);

    const handleTableChangeWrapper = tableState.handleTableChange(columns);

    return (
        <div className='content_container__scrollable'>
            <ListStateIndicator items={tableState.stateIndicators} onReset={tableState.resetState}
                                onRemove={tableState.removeState}/>
            <Table
                bordered
                // tableLayout="fixed"
                // scroll={{x: 1000}}
                columns={controlledColumns}
                dataSource={deviceStatusData}
                loading={loading}
                onChange={handleTableChangeWrapper}
                pagination={{
                    total: totalDevices,
                    current: tableState.currentPage,
                    pageSize: tableState.pageSize,
                    defaultPageSize: defaultPaginationSize,
                    defaultCurrent: 1,
                    showSizeChanger: true,
                    showTotal: (total, range) => `${range[0]} - ${range[1]} / ${total}`,
                    pageSizeOptions: ['10', '20', '50', '100'],
                }}
            />
            {isUserStaff && (
                <Link to="/create/device">
                    <FloatButton
                        type="primary"
                        icon={<FileAddOutlined/>}
                        tooltip={<div>{t('pages.devices.addNewDeviceTitle')}</div>}
                    />
                </Link>
            )}
            {deviceStatusData === [] && (
                <div className="empty_data__overlay">
                    {/*<div className="analysis-card-area__overlay-title">*/}
                    {/*    {t("pages.scan2pay.analysis.messages.warning1")}*/}
                    {/*</div>*/}
                </div>
            )}
        </div>
    );
};

export default Device;

